import type {
  ActivityEntry,
  AssigneeLogEntry,
  ChatMessage,
  DeliverableEntry,
  EstimationMode,
  PimMode,
} from '@/lib/dd-queue/types'

/** Design file — session or server-backed (DD-BE-PR2b-FILES). */
export interface DesignFile {
  id: string
  name: string
  type: string
  size: number
  /** Local just-uploaded preview only; omit for persisted rows. */
  dataUrl?: string
  downloadUrl?: string
  uploadedBy: string
  t: string
}

/** Working / committed proof artifact — mirrors queue-store ProofVersion (demo + 001e). */
export interface ProofSupportingFile {
  id: string
  name: string
  type: string
  size: number
  /** Local just-uploaded preview only; omit for persisted rows. */
  dataUrl?: string
  downloadUrl?: string
}

export interface ProofVersion {
  id: string
  version: number
  name: string
  type: string
  size: number
  dataUrl?: string
  downloadUrl?: string
  uploadedBy: string
  t: string
  deletedAt?: string
  deletedBy?: string
  source?: 'design' | 'estimate'
  supportingFiles?: ProofSupportingFile[]
  /** Frozen at Design complete mint — from API packet_snapshot. */
  packetSnapshot?: Record<string, unknown>
}

/** Session overlay keyed `${requestId}:${sku}` — mirrors queue-store SubjectMutation. */
export interface SubjectMutation {
  deliverableUpdates?: Record<string, Partial<DeliverableEntry>>
  activityHead?: ActivityEntry[]
  deliverableOpenedAt?: Record<string, number>
  deliverableAssigneeLogs?: Record<string, AssigneeLogEntry[]>
  deliverableCompletedAt?: Record<string, number>
  estimationMode?: EstimationMode
  pimMode?: PimMode
  installRequired?: boolean
  attributes?: Record<string, string>
  chat?: ChatMessage[]
  chatReplies?: Record<string, ChatMessage[]>
  nameOverride?: string
  bundleReleased?: boolean
  deletedAt?: string
  deletedBy?: string
  deletedReason?: string
  revisionRound?: number
  designTimerMs?: number
  designTimerStartedAt?: number | null
  /** PR2b-3: per-step work timers; design UI reads `stepTimers.design` (also mirrored to designTimer*). */
  stepTimers?: Record<string, { ms: number; startedAt: number | null }>
  proofVersions?: ProofVersion[]
  quotedProofId?: string
  pmsColors?: string[]
  packetNotes?: string
  /** ROUTING-E1 — PIQ quote commercial fields (line SoT). */
  quoteNumber?: string | null
  unitPriceCents?: number | null
  taxRate?: number | null
  paymentTerms?: string | null
  quoteUrl?: string | null
  /** HS-DEAL-ATTACH-PARSE-002 — parser status / ownership. */
  piqQuoteStatus?: PiqQuoteStatus | null
  piqUnitPriceSource?: PiqUnitPriceSource | null
  /** ROUTING-E3 — HubSpot ticket/deal record-only breadcrumbs (line SoT). */
  hubspotTicketId?: string
  hubspotTicketStage?: string
  /** HS-SYNC-B-001 — resolved label when known (display). */
  hubspotTicketStageLabel?: string
  hubspotDealId?: string
  hubspotDealStage?: string
  /** Active kind=quote file (replace-in-place, no versioning). */
  quoteFile?: DesignFile | null
  filesByCategory?: Record<string, DesignFile[]>
  /** DD-BE-PR2b-1: optimistic locking — line header version from server snapshot. */
  workflowLineVersion?: number
  /** DD-BE-PR2b-1: per-step versions keyed by step_key. */
  workflowStepVersions?: Record<string, number>
  /** DD-DUE-24H-002 — line.due_at from queue-state (SoT once line exists). */
  dueAtIso?: string
}

/** HS-PARSE-CR3 — closed set of parser status / price-source values. */
export type PiqQuoteStatus =
  | 'ok'
  | 'needs_review'
  | 'unrecognized_template'
  | 'parse_failed'

export type PiqUnitPriceSource = 'parser' | 'human'

const PIQ_QUOTE_STATUSES: ReadonlySet<string> = new Set([
  'ok',
  'needs_review',
  'unrecognized_template',
  'parse_failed',
])

const PIQ_UNIT_PRICE_SOURCES: ReadonlySet<string> = new Set(['parser', 'human'])

export function narrowPiqQuoteStatus(value: unknown): PiqQuoteStatus | null {
  return typeof value === 'string' && PIQ_QUOTE_STATUSES.has(value)
    ? (value as PiqQuoteStatus)
    : null
}

export function narrowPiqUnitPriceSource(value: unknown): PiqUnitPriceSource | null {
  return typeof value === 'string' && PIQ_UNIT_PRICE_SOURCES.has(value)
    ? (value as PiqUnitPriceSource)
    : null
}
